/**
 * Pack-board pieces on top of the gate V1 kit (features/gate/v1/kit.tsx) and the processing V1
 * pieces (features/process/v1/ui.tsx): the header with the lot in the corner, the round jobs of
 * the run home, the coder boxes, the hall's figure tiles, the green "done" screen and the
 * yes / back sheet. Sizes and colours are the alignment map's F7 V1 boards'.
 */
import { useState, type ReactNode } from 'react'
import { useSyncStatus } from '@/offline'
import {
  Btn,
  Cta,
  Footer,
  Icon,
  ItemRow,
  SheetActions,
  StepDots,
  V1Screen,
  V1Sheet,
} from '@/features/gate/v1/kit'
import { Rich } from '@/features/gate/v1/Rich'
import { C } from '@/features/gate/v1/style'
import { fmtWhen, tr } from '@/features/gate/v1/text'
import { NumDot } from '@/features/process/v1/ui'
import { WithSite } from '@/features/process/v1/parts'
import type { ProcessState } from '@/features/process/projection'
import { KIcon, PackSprite, type KIconName } from './icons'
import { ki } from './words'
import { useHall } from './useHall'
import type { HeaderLot } from './words'

const FONT = "'Mukta', sans-serif"

// ---------------------------------------------------------------------------------------------
// Data: the site list, with the hall's lists read once online
// ---------------------------------------------------------------------------------------------

/** The site list (or the board asking to download it), with the hall's lists kept fresh. */
export function WithHall({ children }: { children: (state: ProcessState) => ReactNode }) {
  return <WithSite>{(state) => <Hall state={state}>{children}</Hall>}</WithSite>
}

function Hall({
  state,
  children,
}: {
  state: ProcessState
  children: (state: ProcessState) => ReactNode
}) {
  useHall(state)
  return <>{children(state)}</>
}

// ---------------------------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------------------------

const roundBtn = (size: number) =>
  ({
    width: size,
    height: size,
    flexShrink: 0,
    borderRadius: size / 2,
    border: `1.5px solid ${C.border}`,
    background: C.surface,
    color: C.ink,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    boxSizing: 'border-box',
    cursor: 'pointer',
  }) as const

/**
 * The pack boards' header: back, the line or lot (groundnut square), records to send, मदद; then
 * the step icon, the title, an optional quieter line and the n/5 dots.
 */
export function PackHeader({
  onBack,
  lot,
  icon,
  title,
  sub,
  step,
  of = 5,
  iconBg = C.acc,
}: {
  onBack?: () => void
  lot?: HeaderLot
  icon: KIconName
  title: string
  sub?: string
  step?: number
  of?: number
  /** The title icon's circle (amber on the stop board). */
  iconBg?: string
}) {
  const sync = useSyncStatus()
  const [sheet, setSheet] = useState<null | 'help' | 'sync'>(null)
  return (
    <>
      <PackSprite />
      {sheet === 'help' && <HelpSheet onClose={() => setSheet(null)} />}
      {sheet === 'sync' && <SyncSheet onClose={() => setSheet(null)} />}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px 4px' }}>
        {onBack && (
          <button
            type="button"
            aria-label={tr('gate.v1.back')}
            onClick={onBack}
            style={roundBtn(48)}
          >
            <Icon name="back" />
          </button>
        )}
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexGrow: 1, minWidth: 0 }}>
          {lot && (
            <>
              <span
                aria-hidden="true"
                style={{
                  width: 40,
                  height: 40,
                  flexShrink: 0,
                  borderRadius: 10,
                  background: C.sunk,
                  color: C.acc,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon name="groundnut" size={26} />
              </span>
              <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <b
                  style={{
                    fontSize: 17,
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {lot.name}
                </b>
                <span style={{ fontSize: 14, color: C.muted, lineHeight: 1.2 }}>{lot.line}</span>
              </span>
            </>
          )}
        </span>
        <button
          type="button"
          onClick={() => setSheet('sync')}
          aria-label={
            sync.pending > 0
              ? tr('gate.v1.a11y.pending', { count: sync.pending })
              : tr('gate.v1.a11y.allSent')
          }
          style={
            sync.pending > 0
              ? {
                  cursor: 'pointer',
                  height: 36,
                  padding: '0 9px 0 6px',
                  borderRadius: 18,
                  background: C.surface,
                  border: `1.5px solid ${C.border}`,
                  color: C.ink,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                  fontSize: 16,
                  fontWeight: 700,
                  flexShrink: 0,
                  boxSizing: 'border-box',
                  fontFamily: FONT,
                }
              : {
                  border: 0,
                  padding: 0,
                  cursor: 'pointer',
                  width: 36,
                  height: 36,
                  borderRadius: 18,
                  background: C.okWash,
                  color: C.ok,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }
          }
        >
          {sync.pending > 0 ? (
            <>
              <Icon name="clock" size={20} />
              {sync.pending}
            </>
          ) : (
            <Icon name="ticks" size={22} sw={2.2} />
          )}
        </button>
        <button
          type="button"
          onClick={() => setSheet('help')}
          style={{
            height: 48,
            flexShrink: 0,
            padding: '0 12px 0 8px',
            borderRadius: 24,
            border: 0,
            background: C.help,
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontFamily: FONT,
            fontSize: 16,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          <Icon name="info" />
          <span>{tr('gate.v1.help')}</span>
        </button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '2px 14px 6px' }}>
        <span
          aria-hidden="true"
          style={{
            width: 36,
            height: 36,
            flexShrink: 0,
            borderRadius: 18,
            background: iconBg,
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <KIcon name={icon} size={22} />
        </span>
        <span style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: 21, lineHeight: 1.2, fontWeight: 700 }}>{title}</h1>
          {sub && <span style={{ fontSize: 14, color: C.muted, lineHeight: 1.15 }}>{sub}</span>}
        </span>
        {step !== undefined && <StepDots step={step} of={of} />}
      </div>
    </>
  )
}

function HelpSheet({ onClose }: { onClose: () => void }) {
  return (
    <V1Sheet title={tr('gate.v1.help.generalTitle')} onClose={onClose} top={380}>
      <p style={{ margin: 0, fontSize: 18, lineHeight: 1.4 }}>{tr('pack.v1.help')}</p>
    </V1Sheet>
  )
}

/** The header clock: records still on the phone, and अभी भेजें. */
function SyncSheet({ onClose }: { onClose: () => void }) {
  const sync = useSyncStatus()
  const done = sync.pending === 0 && sync.mediaPending === 0
  return (
    <V1Sheet
      title={tr('gate.v1.closeDay.toSend')}
      onClose={onClose}
      top={330}
      actions={
        <Cta
          label={tr('gate.v1.closeDay.sendNow')}
          icon="cloudup"
          busy={sync.phase === 'syncing'}
          onClick={() => void sync.syncNow()}
        />
      }
    >
      <ItemRow
        padding="8px 12px"
        lead={
          <Icon name={done ? 'ticks' : 'clock'} size={28} style={{ color: done ? C.ok : C.ink }} />
        }
        title={
          done
            ? tr('gate.v1.closeDay.allSentDone')
            : tr('gate.v1.closeDay.pending', { count: sync.pending, photos: sync.mediaPending })
        }
        line={
          sync.lastSyncedAt ? (
            <span style={{ color: C.muted }}>
              {tr('gate.v1.sync.last', { when: fmtWhen(sync.lastSyncedAt) })}
            </span>
          ) : undefined
        }
      />
      <p style={{ margin: 0, fontSize: 16, lineHeight: 1.4, color: C.muted }}>
        {tr('gate.v1.sync.offlineOk')}
      </p>
    </V1Sheet>
  )
}

/** The good-stock line under the header ("अच्छा माल · बोरी 3/7 · 133.78 किलो"). */
export function GoodBand({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        background: C.okWash,
        color: C.ok,
        padding: '5px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 16,
        fontWeight: 700,
      }}
    >
      <Icon name="sack" size={22} />
      <span>{children}</span>
    </div>
  )
}

// ---------------------------------------------------------------------------------------------
// Blocks
// ---------------------------------------------------------------------------------------------

/** The icon in a 44px square at the start of a row. */
export function Lead({
  icon,
  bg = C.sunk,
  color = C.acc,
  size = 44,
}: {
  icon: KIconName
  bg?: string
  color?: string
  size?: number
}) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: 10,
        background: bg,
        color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <KIcon name={icon} size={Math.round(size * 0.6)} />
    </span>
  )
}

/** The beige / amber hint with its icon in a white square. */
export function Note({
  icon,
  children,
  tone = 'sunk',
}: {
  icon: KIconName
  children: ReactNode
  tone?: 'sunk' | 'warn'
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'center',
        padding: '8px 12px',
        borderRadius: 12,
        background: tone === 'warn' ? C.warnWash : C.sunk,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: 10,
          background: C.surface,
          color: tone === 'warn' ? C.accDark : C.acc,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <KIcon name={icon} size={24} />
      </span>
      <span style={{ fontSize: 16, lineHeight: 1.35 }}>
        {typeof children === 'string' ? <Rich text={children} /> : children}
      </span>
    </div>
  )
}

/** "अब क्या करें / अब करें" numbered lines. */
export function Steps({
  steps,
  size = 17,
  ring = 26,
}: {
  steps: string[]
  size?: number
  ring?: number
}) {
  return (
    <>
      {steps.map((s, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <NumDot n={i + 1} size={ring} />
          <span style={{ fontSize: size, lineHeight: 1.3 }}>
            <Rich text={s} />
          </span>
        </div>
      ))}
    </>
  )
}

/** One round job of the run home (76px circle, its number, an optional count badge). */
export function JobCircle({
  n,
  icon,
  label,
  done,
  current,
  badge,
  onClick,
}: {
  n: number
  icon: KIconName
  label: string
  done: boolean
  current: boolean
  badge?: number
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={current || undefined}
      style={{
        border: 0,
        background: 'transparent',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        padding: '6px 0',
        cursor: 'pointer',
        fontFamily: FONT,
        color: C.ink,
      }}
    >
      <span
        style={{
          position: 'relative',
          width: 76,
          height: 76,
          borderRadius: 38,
          background: done ? C.okWash : C.sunk,
          color: done ? C.ok : C.acc,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: current ? `0 0 0 3px ${C.acc}` : undefined,
        }}
      >
        <KIcon name={icon} size={40} sw={1.8} />
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: -2,
            top: -2,
            width: 26,
            height: 26,
            borderRadius: 13,
            background: done ? C.ok : C.ink,
            color: '#ffffff',
            fontSize: 15,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {done ? <Icon name="ok" size={16} sw={3} /> : n}
        </span>
        {badge !== undefined && badge > 0 && (
          <span
            style={{
              position: 'absolute',
              right: -4,
              top: -4,
              minWidth: 24,
              height: 24,
              padding: '0 4px',
              boxSizing: 'border-box',
              borderRadius: 12,
              background: C.acc,
              color: '#ffffff',
              fontSize: 15,
              fontWeight: 800,
              border: '2px solid #ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {badge}
          </span>
        )}
      </span>
      <span style={{ fontSize: 17, fontWeight: 600, lineHeight: 1.2 }}>{label}</span>
    </button>
  )
}

/** A line of white boxes with what is keyed into the coder. */
export function CoderBoxes({
  label,
  boxes,
  size,
}: {
  label: string
  boxes: string[]
  size: number
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 15, color: C.muted, fontWeight: 700 }}>{label}</span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
        {boxes.map((b, i) =>
          b === '/' ? (
            <span key={i} style={{ fontSize: size, fontWeight: 800, color: C.muted }}>
              /
            </span>
          ) : (
            <span
              key={i}
              style={{
                padding: '2px 8px',
                borderRadius: 8,
                background: C.surface,
                border: `1.5px solid ${C.border}`,
                fontSize: size,
                fontWeight: 800,
                lineHeight: 1.2,
                wordBreak: 'break-all',
              }}
            >
              {b}
            </span>
          ),
        )}
      </div>
    </div>
  )
}

/** A figure on the beige tile (हॉल में पाउच). */
export function Figure({ value, label }: { value: string; label: string }) {
  return (
    <div
      style={{
        borderRadius: 10,
        background: C.sunk,
        padding: '6px 8px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        minWidth: 0,
      }}
    >
      <b style={{ fontSize: 24, lineHeight: 1.1 }}>{value}</b>
      <span style={{ fontSize: 14, color: C.muted, lineHeight: 1.2 }}>{label}</span>
    </div>
  )
}

/** Label · value rows with an icon (the close sheet). */
export function IconRows({
  rows,
}: {
  rows: Array<{ icon?: KIconName; label: string; value: string }>
}) {
  return (
    <section
      style={{
        background: C.surface,
        border: `1.5px solid ${C.rule}`,
        borderRadius: 12,
        padding: '8px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      {rows.map((r) => (
        <div
          key={r.label}
          style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 17 }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {r.icon && (
              <span style={{ color: C.acc, display: 'flex' }}>
                <KIcon name={r.icon} size={22} />
              </span>
            )}
            {r.label}
          </span>
          <b style={{ whiteSpace: 'nowrap' }}>{r.value}</b>
        </div>
      ))}
    </section>
  )
}

/** The amber "cannot change after" note. */
export function Locked({ children }: { children: string }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'center',
        padding: '8px 12px',
        borderRadius: 12,
        background: C.warnWash,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 28,
          height: 28,
          flexShrink: 0,
          borderRadius: 4,
          background: C.acc,
          color: '#ffffff',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name="bang" size={19} sw={3} />
      </span>
      <span style={{ fontSize: 16, lineHeight: 1.35, color: C.ink }}>
        <Rich text={children} />
      </span>
    </div>
  )
}

/** The yes / back sheet (पैकिंग शुरू करें?, रन बंद करें?, माल रवाना करें?). */
export function YesSheet({
  title,
  yes,
  yesIcon,
  busy,
  onYes,
  onClose,
  children,
  top = 200,
}: {
  title: string
  yes: string
  yesIcon: KIconName
  busy?: boolean
  onYes: () => void
  onClose: () => void
  children: ReactNode
  top?: number
}) {
  return (
    <V1Sheet
      title={title}
      onClose={onClose}
      top={top}
      actions={
        <SheetActions>
          <Btn icon="back" onClick={onClose}>
            {tr('gate.v1.back')}
          </Btn>
          <Cta label={yes} icon={ki(yesIcon)} busy={busy} onClick={onYes} />
        </SheetActions>
      }
    >
      <PackSprite />
      {children}
    </V1Sheet>
  )
}

/** The green full screen after a close or a dispatch, with what to do now. */
export function DoneScreen({
  title,
  lines,
  todo,
  cta,
}: {
  title: string
  lines: string[]
  todo: string[]
  cta: ReactNode
}) {
  return (
    <V1Screen main={{ padding: '0', gap: 0 }} footer={<Footer>{cta}</Footer>}>
      <PackSprite />
      <div
        role="status"
        style={{
          flexGrow: 1,
          background: C.ok,
          color: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          padding: 20,
          textAlign: 'center',
        }}
      >
        <span
          style={{
            width: 110,
            height: 110,
            borderRadius: 55,
            background: '#ffffff',
            color: C.ok,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name="ok" size={70} sw={3.2} />
        </span>
        <h1 style={{ margin: 0, fontSize: 30 }}>{title}</h1>
        {lines.map((l) => (
          <span key={l} style={{ fontSize: 20, lineHeight: 1.3 }}>
            {l}
          </span>
        ))}
        {todo.length > 0 && (
          <div
            style={{
              marginTop: 8,
              alignSelf: 'stretch',
              borderRadius: 12,
              background: C.surface,
              color: C.ink,
              padding: '10px 12px',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              textAlign: 'left',
            }}
          >
            <b style={{ fontSize: 17 }}>{tr('pack.v1.todo')}</b>
            <Steps steps={todo} ring={26} />
          </div>
        )}
      </div>
    </V1Screen>
  )
}

/** A big white button (the +1 carton). */
export function BigButton({
  icon,
  title,
  line,
  onClick,
}: {
  icon: KIconName
  title: string
  line: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        minHeight: 84,
        borderRadius: 12,
        border: `2.5px solid ${C.acc}`,
        background: C.surface,
        color: C.ink,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        padding: '8px 12px',
        fontFamily: FONT,
        cursor: 'pointer',
        width: '100%',
      }}
    >
      <span style={{ color: C.acc, display: 'flex' }}>
        <KIcon name={icon} size={40} sw={1.8} />
      </span>
      <span
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          lineHeight: 1.15,
        }}
      >
        <b style={{ fontSize: 28 }}>{title}</b>
        <span style={{ fontSize: 15, color: C.muted }}>{line}</span>
      </span>
    </button>
  )
}
